<script setup lang="ts" generic="T extends Record<string, any>">
import { computed } from "vue";
import { Icon } from "@iconify/vue";
import { useI18n } from "vue-i18n";
import { DATA_TABLE_SIZES, useDataTableWidget } from "../composables/useDataTableWidget";
import ColumnSettingsWidget from "./ColumnSettingsWidget.vue";
import FormFieldsWidget from "./FormFieldsWidget.vue";

const props = defineProps<{
  table: ReturnType<typeof useDataTableWidget<T>>;
}>();

const { t } = useI18n();

const sizes = computed(() =>
  DATA_TABLE_SIZES.map((s) => ({
    value: s,
    label: t(`common.${s}`),
  }))
);
</script>

<template>
  <!-- Target teleport bisa diatur lewat param `filterTeleportTo` (id string / selector / ref).
       v-if memastikan panel baru dirender setelah target tersedia (penting untuk ref). -->
  <Teleport v-if="table.filterTeleportTarget.value" defer :to="table.filterTeleportTarget.value">
    <div v-if="table.isFilterOpen.value" class="w-[260px] h-full border-r border-gray-200/50 flex flex-col">
      <div class="flex px-3 py-4">
        <h4 class="font-semibold">{{ t("common.filter") }}</h4>
      </div>
      <div class="grow px-3 overflow-y-auto">
        <n-form @submit.prevent="table.applyFilter">
          <FormFieldsWidget v-model:value="table.filterModel" :fields="table.filterFields"></FormFieldsWidget>
        </n-form>
      </div>
      <div class="flex gap-2 px-3 py-4">
        <div class="shrink-0">
          <n-button type="default" @click.stop="table.resetFilter">{{ t("common.actions.reset") }}</n-button>
        </div>
        <div class="grow">
          <n-button type="primary" block @click.stop="table.applyFilter">{{ t("common.filter") }}</n-button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- Toolbar -->
  <div class="flex mb-3 gap-2">
    <n-badge :value="table.activeFilterCount.value" :max="15" :show="table.activeFilterCount.value > 0" type="info">
      <n-button @click.stop="table.toggleFilter" type="default" :secondary="table.isFilterOpen.value">
        <template #icon>
          <Icon v-if="table.isFilterOpen.value" icon="mdi:close" />
          <Icon v-else icon="mdi:filter" />
        </template>
      </n-button>
    </n-badge>

    <n-input-group>
      <n-input :placeholder="t('common.searchPlaceholder')" v-model:value="table.search.value" clearable @keyup.enter="table.onSearch(table.search.value)" @clear="table.onSearch('')"> </n-input>

      <n-button @click="table.onSearch(table.search.value)">
        <template #icon>
          <Icon icon="mdi:magnify" />
        </template>
      </n-button>
    </n-input-group>

    <n-button type="default" @click.stop="table.refresh">
      <template #icon>
        <Icon icon="mdi:refresh" />
      </template>
    </n-button>

    <n-button v-if="table.isExportEnabled" type="default" @click.stop="table.openExportModal">
      <template #icon>
        <Icon icon="mdi:download" />
      </template>
    </n-button>

    <n-button type="default" @click.stop="table.openColumnSettings">
      <template #icon>
        <Icon icon="mdi:cog" />
      </template>
    </n-button>

    <!-- Tombol tambahan (conditional lewat action.visible), sejajar tombol "+" -->
    <template v-for="action in table.actions" :key="action.key">
      <n-button v-if="table.isActionVisible(action)" :type="action.type ?? 'default'" @click.stop="action.onClick">
        <template v-if="action.icon" #icon>
          <Icon :icon="action.icon" />
        </template>
        {{ action.label }}
      </n-button>
    </template>

    <n-button type="success" v-if="!!table.create?.enable" @click="table.create.onClick">
      <template #icon>
        <Icon icon="mdi:plus" />
      </template>
    </n-button>
  </div>

  <!-- Modal pengaturan kolom (dibuka dari tombol cog di toolbar) -->
  <n-modal
    v-model:show="table.isShowVisibleColumnModal.value"
    class="custom-card"
    preset="card"
    :style="{}"
    :title="t('common.settings')"
    :bordered="false"
    style="width: 640px"
    :segmented="{
      content: false,
      footer: false,
    }"
    @update:show="
      (v: boolean) => {
        if (!v) table.cancelColumnVisibility();
      }
    "
  >
    <!-- Pengaturan ukuran tabel (naive-ui: small | medium | large) -->
    <div class="mb-5">
      <p class="font-medium mb-2">{{ t("common.size") }}</p>

      <n-radio-group :value="table.size.value" @update:value="table.setSize">
        <div class="flex gap-5">
          <n-radio v-for="s in sizes" :key="s.value" :value="s.value" :label="s.label" />
        </div>
      </n-radio-group>
    </div>

    <!-- Pengaturan kolom -->
    <p class="font-medium mb-2">{{ t("common.fieldSettings") }}</p>

    <ColumnSettingsWidget
      :options="table.draftColumnOptions.value"
      :visible-keys="table.draftVisibleColumns.value"
      :available-title="t('common.hidden')"
      :displayed-title="t('common.display')"
      @update:visible-keys="table.setDraftVisibleColumns"
    />
    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <n-button type="default" @click="table.cancelColumnVisibility"> {{ t("common.actions.cancel") }} </n-button>
        <n-button type="primary" @click="table.confirmColumnVisibility"> {{ t("common.actions.confirm") }} </n-button>
      </div>
    </template>
  </n-modal>

  <!-- Modal export: filter default kosong (ditambahkan lewat popup checkbox),
       di atasnya pilihan kolom untuk export (id / uniqueKey wajib). -->
  <n-modal
    v-model:show="table.isShowExportModal.value"
    class="custom-card"
    preset="card"
    :title="t('common.export')"
    :bordered="false"
    style="width: 640px"
    :segmented="{
      content: false,
      footer: false,
    }"
    @update:show="
      (v: boolean) => {
        if (!v) table.cancelExport();
      }
    "
  >
    <!-- Filter (default tidak ada): tambah hapus filter lewat popup checkbox -->
    <div v-if="table.filterFields.length > 0" class="mb-5">
      <div class="flex items-center justify-between mb-2">
        <p class="font-medium">
          {{ t("common.filter") }}
          <span v-if="table.draftExportFilterFields.value.length > 0" class="text-gray-400 font-normal">({{ table.draftExportFilterFields.value.length }})</span>
        </p>

        <n-popover trigger="click" placement="bottom-end" :show-arrow="true">
          <template #trigger>
            <n-button size="small" quaternary>
              <template #icon>
                <Icon icon="mdi:filter-plus" />
              </template>
              {{ t("common.actions.addFilter") }}
            </n-button>
          </template>

          <n-checkbox-group :value="table.draftExportFilterFields.value" @update:value="table.setExportFilterFields">
            <div class="flex flex-col gap-2 w-56">
              <n-checkbox v-for="field in table.filterFields" :key="field.name" :value="field.name" :label="field.label" />
            </div>
          </n-checkbox-group>
        </n-popover>
      </div>

      <!-- Form field filter yang sudah ditambahkan -->
      <div v-if="table.exportFilterFields.value.length > 0" class="border rounded-md p-3">
        <n-form @submit.prevent>
          <FormFieldsWidget v-model:value="table.exportFilterModel" :fields="table.exportFilterFields.value"></FormFieldsWidget>
        </n-form>
      </div>
    </div>

    <!-- Pilih kolom untuk export (id wajib; grid 3 kolom) -->
    <div>
      <p class="font-medium mb-2">{{ t("common.selectColumnsToExport") }}</p>

      <div class="border rounded-md p-3">
        <div class="grid grid-cols-3 gap-y-2">
          <n-checkbox
            v-for="opt in table.exportColumnOptions.value"
            :key="opt.key"
            :checked="table.draftExportColumns.value.includes(opt.key)"
            :disabled="opt.locked || (table.draftExportColumns.value.length <= 1 && !opt.locked)"
            @update:checked="(v: boolean) => table.toggleExportColumn(opt.key, v)"
          >
            {{ opt.label }}
          </n-checkbox>
        </div>
      </div>

      <!-- Sheet rincian (relasi one-to-many): opsional, grid 3 kolom -->
      <template v-if="table.exportDetailSheetOptions.value.length > 0">
        <p class="font-medium mb-2 mt-4">{{ t("common.selectDetailSheets") }}</p>

        <div class="border rounded-md p-3">
          <div class="grid grid-cols-3 gap-y-2">
            <n-checkbox
              v-for="opt in table.exportDetailSheetOptions.value"
              :key="opt.key"
              :checked="table.draftExportSheets.value.includes(opt.key)"
              @update:checked="(v: boolean) => table.toggleExportSheet(opt.key, v)"
            >
              {{ opt.label }}
            </n-checkbox>
          </div>
        </div>
      </template>
    </div>

    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <n-button type="default" @click="table.cancelExport"> {{ t("common.actions.cancel") }} </n-button>
        <n-button type="primary" @click="table.confirmExport"> {{ t("common.export") }} </n-button>
      </div>
    </template>
  </n-modal>
</template>
